import { createAction } from '@reduxjs/toolkit';
import { showNotificationAsync } from '../notifications/actions';
import { MAX_WALLET_ATTEMPTS } from './constants';
import {
    selectCpaProductsLoading,
    selectCpaProductsRecomendationsLoading,
    selectCpaProductsSuggestLoading,
    selectCpaShopsLoading,
    selectCpaShopsRecomendationsLoading,
    selectCpaShopsSuggestLoading,
} from './selectors';
import {
    loadCpaProducts,
    loadCpaProductsRecommendations,
    loadCpaProductsSuggest,
    loadCpaShops,
    loadCpaShopsRecomendations,
    loadCpaShopsSuggest,
    loadWalletInfo,
} from './service';
import { CpaProductsModel, CpaShopsModel, CpaSearchSuggestModel, WalletInfoModel } from './types';
import type { RootState } from '../store';
import type { AnyAction } from 'redux';
import type { ThunkAction } from 'redux-thunk';

export const setWalletInfo = createAction<WalletInfoModel>('@@cpa/SET_WALLET_INFO');

export const setWalletInfoLoading = createAction<boolean>('@@cpa/SET_WALLET_INFO_LOADING');

export const incrementWalletAttempts = createAction('@@cpa/INCREMENT_WALLET_ATTEMPTS');

export const resetWalletAttempts = createAction('@@cpa/RESET_WALLET_ATTEMPTS');

export const setCpaShopsLoading = createAction<boolean>('@@cpa/SET_CPA_SHOPS_LOADING');

export const setCpaShops = createAction<CpaShopsModel>('@@cpa/SET_CPA_SHOPS');

export const setCpaShopsSuggestLoading = createAction<boolean>(
    '@@cpa/SET_CPA_SHOPS_SUGGEST_LOADING',
);

export const setCpaShopsSuggest = createAction<CpaSearchSuggestModel[]>(
    '@@cpa/SET_CPA_SHOPS_SUGGEST',
);

export const setCpaShopsRecomendationsLoading = createAction<boolean>(
    '@@cpa/SET_CPA_SHOPS_RECOMENDATIONS_LOADING',
);

export const setCpaShopsRecomendations = createAction<CpaShopsModel>(
    '@@cpa/SET_CPA_SHOPS_RECOMENDATIONS',
);

export const setCpaProductsRecomendationsLoading = createAction<boolean>(
    '@@cpa/SET_CPA_PRODUCTS_RECOMENDATIONS_LOADING',
);

export const setCpaProductsRecomendations = createAction<CpaProductsModel>(
    '@@cpa/SET_CPA_PRODUCTS_RECOMENDATIONS',
);

export const setCpaProductsLoading = createAction<boolean>('@@cpa/SET_CPA_PRODUCTS_LOADING');

export const setCpaProducts = createAction<CpaProductsModel>('@@cpa/SET_CPA_PRODUCTS');

export const setCpaProductsSuggestLoading = createAction<boolean>(
    '@@cpa/SET_CPA_PRODUCTS_SUGGEST_LOADING',
);

export const setCpaProductsSuggest = createAction<CpaSearchSuggestModel[]>(
    '@@cpa/SET_CPA_PRODUCTS_SUGGEST',
);

export function loadWalletInfoAsync(): ThunkAction<
    Promise<WalletInfoModel | undefined>,
    RootState,
    undefined,
    AnyAction
> {
    return async (dispatch, getState) => {
        const state = getState();

        if (state.cpa?.isLoading) {
            return;
        }

        const initialAttemptCount = state.cpa?.attemptCount ?? 0;
        if (initialAttemptCount >= MAX_WALLET_ATTEMPTS) {
            return;
        }

        dispatch(setWalletInfoLoading(true));

        for (let i = initialAttemptCount; i < MAX_WALLET_ATTEMPTS; i++) {
            dispatch(incrementWalletAttempts());

            try {
                const walletInfo = await loadWalletInfo();
                dispatch(setWalletInfo(walletInfo));
                dispatch(resetWalletAttempts());
                dispatch(setWalletInfoLoading(false));

                return walletInfo;
            } catch (_error) {
                if (i + 1 >= MAX_WALLET_ATTEMPTS) {
                    dispatch(showNotificationAsync('Не удалось загрузить данные кошелька'));
                    break;
                }
            }
        }

        dispatch(setWalletInfoLoading(false));
    };
}

export function loadCpaShopsAsync(
    text: string,
    page?: number,
): ThunkAction<void, RootState, undefined, AnyAction> {
    return async (dispatch, getState) => {
        try {
            const state = getState();
            const isLoading = selectCpaShopsLoading(state);

            if (isLoading) {
                return;
            }

            await dispatch(setCpaShopsLoading(true));

            const response = await loadCpaShops(text, page);

            dispatch(setCpaShops(response));
        } catch (_error) {
            dispatch(showNotificationAsync('Не удалось загрузить список магазинов'));
        } finally {
            await dispatch(setCpaShopsLoading(false));
        }
    };
}

export function loadCpaShopsSuggestAsync(
    text: string,
): ThunkAction<void, RootState, undefined, AnyAction> {
    return async (dispatch, getState) => {
        try {
            const state = getState();
            const isLoading = selectCpaShopsSuggestLoading(state);

            if (isLoading) {
                return;
            }

            await dispatch(setCpaShopsSuggestLoading(true));

            const response = await loadCpaShopsSuggest(text);

            dispatch(setCpaShopsSuggest(response));
        } catch (_error) {
            dispatch(showNotificationAsync('Не удалось загрузить саджест'));
        } finally {
            await dispatch(setCpaShopsSuggestLoading(false));
        }
    };
}

export function loadCpaShopsRecomendationsAsync(
    page?: number,
): ThunkAction<void, RootState, undefined, AnyAction> {
    return async (dispatch, getState) => {
        try {
            const state = getState();
            const isLoading = selectCpaShopsRecomendationsLoading(state);

            if (isLoading) {
                return;
            }

            await dispatch(setCpaShopsRecomendationsLoading(true));

            const response = await loadCpaShopsRecomendations(page);

            dispatch(setCpaShopsRecomendations(response));
        } catch (_error) {
            dispatch(showNotificationAsync('Не удалось загрузить список рекомендаций'));
        } finally {
            await dispatch(setCpaShopsRecomendationsLoading(false));
        }
    };
}

export function loadCpaProductsRecomendationsAsync(
    page?: number,
): ThunkAction<void, RootState, undefined, AnyAction> {
    return async (dispatch, getState) => {
        try {
            const state = getState();
            const isLoading = selectCpaProductsRecomendationsLoading(state);

            if (isLoading) {
                return;
            }

            await dispatch(setCpaProductsRecomendationsLoading(true));

            const response = await loadCpaProductsRecommendations(page);

            dispatch(setCpaProductsRecomendations(response));
        } catch (_error) {
            dispatch(showNotificationAsync('Не удалось загрузить список рекомендаций'));
        } finally {
            await dispatch(setCpaProductsRecomendationsLoading(false));
        }
    };
}

export function loadCpaProductsAsync(
    text: string,
    page?: number,
): ThunkAction<void, RootState, undefined, AnyAction> {
    return async (dispatch, getState) => {
        try {
            const state = getState();
            const isLoading = selectCpaProductsLoading(state);

            if (isLoading) {
                return;
            }

            await dispatch(setCpaProductsLoading(true));

            const response = await loadCpaProducts(text, page);

            dispatch(setCpaProducts(response));
        } catch (_error) {
            dispatch(showNotificationAsync('Не удалось загрузить список товаров'));
        } finally {
            await dispatch(setCpaProductsLoading(false));
        }
    };
}

export function loadCpaProductsSuggestAsync(
    text: string,
): ThunkAction<void, RootState, undefined, AnyAction> {
    return async (dispatch, getState) => {
        try {
            const state = getState();
            const isLoading = selectCpaProductsSuggestLoading(state);

            if (isLoading) {
                return;
            }

            await dispatch(setCpaProductsSuggestLoading(true));

            const response = await loadCpaProductsSuggest(text);

            dispatch(setCpaProductsSuggest(response));
        } catch (_error) {
            dispatch(showNotificationAsync('Не удалось загрузить саджест'));
        } finally {
            await dispatch(setCpaProductsSuggestLoading(false));
        }
    };
}
